/**
 * Rights-Attach Dashboard — per-brand queue of renders still missing a
 * licence URL / release doc. Growth Operators batch-fill paperwork here
 * before campaigns are handed off to SocialPulse.
 *
 * Backend: GET  /api/visual-studio/rights/pending/queue  →  grouped list
 *          POST /api/visual-studio/rights/attach        →  attach rights
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  FileCheck2, ShieldAlert, Loader2, RefreshCw, Filter, ExternalLink,
  Building2, Image as ImageIcon, Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RightsAttachModal from "@/components/RightsAttachModal";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, Icon, tone = "slate" }) {
  const tones = {
    slate:   "bg-slate-100 text-slate-700",
    amber:   "bg-amber-100 text-amber-700",
    rose:    "bg-rose-100 text-rose-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`rights-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon size={16} />
      </div>
      <div className="mt-3 text-2xl font-extrabold text-slate-900 leading-none">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mt-1">{label}</div>
    </div>
  );
}

export default function RightsQueue() {
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");           // all | generated | licensed
  const [modal, setModal] = useState(null);              // { postId, size, origin }

  usePageSeo({
    title: "Rights Attach Queue — Batch Paperwork for Pending Creatives | WebsiteTech.pro",
    description: "Growth Operator queue of AI-generated and licensed renders that still need licence URLs or model-release docs. Attach paperwork once, flow it through every SocialPulse handoff.",
    noindex: true,
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Visual Studio", url: "/visual-studio" },
    { name: "Rights Queue", url: "/rights-queue" },
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("wt_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API}/visual-studio/rights/pending/queue?limit=200`, { headers });
      setQueue(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error("Please log in to view the rights queue");
      } else {
        toast.error(e?.response?.data?.detail || "Could not load queue");
      }
      setQueue({ ok: false, counts: {}, items: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    const items = queue?.items || [];
    if (filter === "all") return items;
    return items.filter((i) => i.origin === filter);
  }, [queue, filter]);

  const byBrand = useMemo(() => {
    const map = new Map();
    for (const it of filteredItems) {
      const key = it.brand_id || "_none";
      if (!map.has(key)) map.set(key, { brand_id: it.brand_id, brand_name: it.brand_name || "Unassigned brand", items: [] });
      map.get(key).items.push(it);
    }
    return Array.from(map.values());
  }, [filteredItems]);

  const counts = queue?.counts || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="pt-28 pb-20" data-testid="rights-queue-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand flex items-center gap-2">
                <FileCheck2 size={13} /> Rights-Attach Queue
              </p>
              <h1 className="mt-2 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl leading-tight">
                Clear every pending licence<br />
                <span className="text-brand">before SocialPulse ships it.</span>
              </h1>
              <p className="mt-3 text-base text-slate-600 max-w-2xl">
                Every render tagged <strong>AI-generated</strong> or <strong>Licensed stock</strong> enters this queue
                until paperwork is attached. Attach once, and the paper trail flows into every subsequent
                SocialPulse handoff receipt — automatically.
              </p>
            </div>
            <button
              data-testid="rights-queue-refresh"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-brand hover:text-brand disabled:opacity-60"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Refresh
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Pending" value={counts.pending || 0} Icon={ShieldAlert} tone={counts.pending ? "amber" : "emerald"} />
            <StatCard label="AI-generated" value={counts.generated || 0} Icon={Sparkles} tone="rose" />
            <StatCard label="Licensed stock" value={counts.licensed || 0} Icon={ImageIcon} tone="slate" />
            <StatCard label="Brands affected" value={counts.brands || 0} Icon={Building2} tone="slate" />
          </div>

          <div className="mt-6 flex items-center gap-2" data-testid="rights-queue-filters">
            <Filter size={14} className="text-slate-400" />
            {[
              { k: "all",       label: `All (${counts.pending || 0})` },
              { k: "generated", label: `AI-generated (${counts.generated || 0})` },
              { k: "licensed",  label: `Licensed (${counts.licensed || 0})` },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`rights-filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === f.k ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="mt-6">
            {loading ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400" data-testid="rights-queue-loading">
                <Loader2 size={20} className="mx-auto animate-spin" />
                <p className="mt-2 text-sm">Loading rights queue…</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-10 text-center" data-testid="rights-queue-empty">
                <FileCheck2 size={28} className="mx-auto text-emerald-600" />
                <h3 className="mt-3 font-bold text-emerald-800">Every render has paperwork attached.</h3>
                <p className="mt-1 text-sm text-emerald-700">
                  New AI-generated or licensed creatives will land here automatically.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {byBrand.map((group) => (
                  <section key={group.brand_id || "_none"} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`rights-brand-${group.brand_id || "none"}`}>
                    <header className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-slate-500" />
                        <h3 className="font-bold text-slate-900 text-sm">{group.brand_name}</h3>
                        <span className="text-[11px] font-mono text-slate-400">{group.brand_id || "no-brand"}</span>
                      </div>
                      <span className="text-xs font-semibold text-amber-700">
                        {group.items.length} pending
                      </span>
                    </header>
                    <ul className="divide-y divide-slate-100">
                      {group.items.map((it) => (
                        <li
                          key={`${it.post_id}_${it.size}`}
                          data-testid={`rights-item-${it.post_id}-${it.size}`}
                          className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50"
                        >
                          <div className="w-16 h-16 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                            {it.render_url ? (
                              <img src={it.render_url} alt={`render ${it.size}`} className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon size={20} className="text-slate-300" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${it.origin === "generated" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                                {it.origin}
                              </span>
                              <span className="text-[10px] font-mono text-slate-400">{it.size}</span>
                              {it.approved && (
                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">approved</span>
                              )}
                            </div>
                            <p className="mt-1 text-sm font-medium text-slate-800 truncate">
                              {it.headline || it.campaign_name}
                            </p>
                            <p className="text-[11px] text-slate-400 truncate">
                              Campaign: {it.campaign_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {it.render_url && (
                              <a
                                href={it.render_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-brand"
                                data-testid={`rights-view-${it.post_id}-${it.size}`}
                              >
                                <ExternalLink size={12} /> View
                              </a>
                            )}
                            <button
                              data-testid={`rights-attach-${it.post_id}-${it.size}`}
                              onClick={() => setModal({ postId: it.post_id, size: it.size, origin: it.origin })}
                              className="btn-primary rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
                            >
                              <FileCheck2 size={12} /> Attach
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>

          <p className="mt-10 text-xs text-slate-400">
            Need to add rights ad-hoc? Open any campaign in{" "}
            <Link to="/visual-studio" className="underline text-brand">Visual Studio</Link> and click a flagged render.
            Rights automatically flow into every outgoing{" "}
            <span className="font-mono">POST /api/socialpulse/handoff</span> payload.
          </p>
        </div>
      </main>
      <Footer />

      {modal && (
        <RightsAttachModal
          open
          postId={modal.postId}
          size={modal.size}
          origin={modal.origin}
          current={null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
